// Settings → Cash hand-over (0353, 0361, 0364).
//
// Three rules about the cash a therapist hands her rider, all Leon's call on
// 2026-10-22 and all flagged to Luigi, because the Cash Handling Rules name no
// distance and allow the hand-over "during pickup or during drop-off, whichever
// is safer". So each is a setting, not code: a different answer is a toggle.
//
//   · the person-to-person distance check (on, 50 m);
//   · the rider → hub distance check (off — a hub pin is often rough);
//   · the cash must be in the rider's hand before her Return pick-up (on).

import { Button } from "@bednbody/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@bednbody/ui/components/card";
import { Switch } from "@bednbody/ui/components/switch";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { type OperatingSettings, useUpdateOperatingSettings } from "./settings.hooks";
import { Field, NumberInput } from "./settingsFields";

interface Draft {
  personOn: boolean;
  personRange: string;
  hubOn: boolean;
  hubRange: string;
  beforePickup: boolean;
}

function draftFrom(settings: OperatingSettings): Draft {
  return {
    personOn: settings.cash_handover_proximity_enabled,
    personRange: String(settings.cash_handover_range_m),
    hubOn: settings.hub_cash_proximity_enabled,
    hubRange: String(settings.hub_cash_range_m),
    beforePickup: settings.cash_before_pickup_required,
  };
}

function ToggleRow({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="space-y-0.5">
        <p className="text-sm font-medium text-foreground">{label}</p>
        <p className="text-sm text-muted-foreground">{hint}</p>
      </div>
      <Switch aria-label={label} checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

export function CashHandoverCard({ settings }: { settings: OperatingSettings }) {
  const update = useUpdateOperatingSettings();
  const [draft, setDraft] = useState<Draft>(() => draftFrom(settings));

  useEffect(() => {
    setDraft(draftFrom(settings));
  }, [settings]);

  const personRange = Number(draft.personRange);
  const hubRange = Number(draft.hubRange);
  // The database refuses outside these (0353); checked here so the reason is
  // readable before the round trip.
  const personValid = Number.isInteger(personRange) && personRange >= 10 && personRange <= 1000;
  const hubValid = Number.isInteger(hubRange) && hubRange >= 10 && hubRange <= 2000;

  function save() {
    if (!personValid || !hubValid) {
      toast.error("Ranges are whole metres: 10–1000 between people, 10–2000 to the hub.");
      return;
    }
    update.mutate(
      {
        cash_handover_proximity_enabled: draft.personOn,
        cash_handover_range_m: personRange,
        hub_cash_proximity_enabled: draft.hubOn,
        hub_cash_range_m: hubRange,
        cash_before_pickup_required: draft.beforePickup,
      },
      {
        onSuccess: () => toast.success("Cash hand-over saved."),
        onError: (error) => toast.error(error.message),
      },
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Cash hand-over</CardTitle>
        <CardDescription>
          When a therapist hands cash to her rider. Not in the Cash Handling Rules, which allow it
          at pick-up or drop-off — these are Leon&apos;s settings from 22 Oct, pending Luigi.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <ToggleRow
          label="Check the distance between therapist and rider"
          hint="Both must be close, with a location from the last few seconds, before either confirms the cash."
          checked={draft.personOn}
          onChange={(personOn) => setDraft((d) => ({ ...d, personOn }))}
        />
        <Field label="Range between them (metres, 10–1000)">
          <NumberInput
            value={draft.personRange}
            onChange={(personRange) => setDraft((d) => ({ ...d, personRange }))}
          />
        </Field>

        <ToggleRow
          label="Check the distance to the hub"
          hint="The rider must be near the hub's pin to hand cash in. Off by default: hub pins are often rough."
          checked={draft.hubOn}
          onChange={(hubOn) => setDraft((d) => ({ ...d, hubOn }))}
        />
        <Field label="Range to the hub (metres, 10–2000)">
          <NumberInput
            value={draft.hubRange}
            onChange={(hubRange) => setDraft((d) => ({ ...d, hubRange }))}
          />
        </Field>

        <ToggleRow
          label="Cash before her ride home"
          hint="Her Return pick-up waits until the rider has confirmed the cash. Off = cash at pick-up or drop-off."
          checked={draft.beforePickup}
          onChange={(beforePickup) => setDraft((d) => ({ ...d, beforePickup }))}
        />
      </CardContent>
      <CardFooter>
        <Button onClick={save} disabled={update.isPending || !personValid || !hubValid}>
          Save changes
        </Button>
      </CardFooter>
    </Card>
  );
}
